import { useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import { Button, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/AddTwoTone';
import RemoveIcon from '@mui/icons-material/RemoveCircleOutlined';
import { QueryFilter } from '@api/Trinium/types';
import { useFetchCategoriesQuery } from '@api/Trinium/Endpoints/JobCategories/slice';
import { FilterOperator, FilterValue } from '@components/Filters/types';
import TextField from '@forms/Components/TextInput';
import { NumberField } from '@forms/Components/NumberInput';
import RadioGroupField from '@forms/Components/RadioGroup';
import { AppAutocomplete, AppComplexAutocomplete } from '@forms/Components/AutocompleteInput';
import { AppMultiSelectChip, AppMultiSelectChipComplex } from '@forms/Components/MultiSelectChipInput';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { BoardQueryFormFields } from './types';
import FilterValueLookup from './FilterLookups/FilterValueLookup';
import { blankLookupItem } from './FilterLookups/types';
import {
  blankOperatorOption,
  blankPickerLabel,
  blankPickerValue,
  describeFilter,
  FilterFieldOption,
  filterFieldOptions,
  FilterMapKey,
  FilterValueOption,
  formatDayOffset,
  getOperatorOptions,
  getValueOperatorPhrase,
  isMultiValueOperator,
  isSameFilter
} from './filters';

import styles from './Page.module.css';

const dayOffsetHint = '0 is today, 1 is tomorrow, -1 is yesterday';

/** Radio options for a boolean filter. The first is the affirmative one, which is what the saved `true` means. */
const booleanOptions = ['Yes', 'No'];

/** The blank choice as a closed set sees it. Leads the list, the same as it does in a lookup. */
const blankValueOption: FilterValueOption = { value: blankPickerValue, label: blankPickerLabel };

const getValueOptionLabel = (option: unknown) => (option as FilterValueOption).label;

const isValueOptionEqual = (option: unknown, candidate: unknown) =>
  (option as FilterValueOption).value === (candidate as FilterValueOption).value;

/**
 * A closed-set option. Values the board shows as a row color carry that color, so the option is recognizable as the
 * thing on screen rather than as the number underneath it — which is what Leg Priority would otherwise be picked by.
 */
const renderValueOption = ({ key, ...optionProps }: React.HTMLAttributes<HTMLLIElement> & { key: unknown }, option: unknown) => {
  const { label, color } = option as FilterValueOption;

  return (
    <li
      key={String(key)}
      {...optionProps}
      className={`${optionProps.className ?? ''} ${styles['value-option']}`}>
      {color && (
        <span
          className={styles['value-swatch']}
          style={{ backgroundColor: color }}
        />
      )}
      {label}
    </li>
  );
};

/**
 * Assembles the value to save from whichever input the field type put on screen. Returns undefined when the draft
 * cannot be saved yet, which is what holds the Add button closed — an unset filter value is not a thing worth saving.
 */
function buildFilterValue(
  field: FilterFieldOption,
  operator: FilterOperator,
  singleValue: string,
  multiValues: string[]
): FilterValue | undefined {
  if (field.fieldType === 'boolean') return singleValue === booleanOptions[0];

  if (isMultiValueOperator(operator)) {
    if (multiValues.length === 0) return undefined;
    if (field.fieldType !== 'number') return multiValues;

    const numbers = multiValues.map(Number);
    return numbers.some(Number.isNaN) ? undefined : numbers;
  }

  const trimmed = singleValue.trim();
  if (!trimmed) return undefined;

  if (field.fieldType === 'date') {
    // The date operators add this to today with dayjs' day unit, so a fraction of a day would be meaningless
    const offset = Number(trimmed);
    return Number.isInteger(offset) ? offset : undefined;
  }

  if (field.fieldType === 'number') {
    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? undefined : parsed;
  }

  return trimmed;
}

/**
 * The filters the query narrows the board by, alongside a builder for adding another. Filters are all-or-nothing —
 * every one must hold for a leg to show — so two filters on the same field are how a range gets expressed, and the
 * builder keeps the field and operator selected after an add to make that pair quick to enter.
 *
 * The draft is deliberately component state rather than form state: a half-entered filter must not mark the query
 * form dirty, which would arm the unsaved-changes guard and light up Save for something never added.
 */
export default function FilterSelection() {
  const { values, errors, setFieldValue } = useFormikContext<BoardQueryFormFields>();
  const { number: terminalNumber } = useCurrentTerminal();

  const [fieldKey, setFieldKey] = useState<FilterMapKey | null>(null);
  // Held as the phrase rather than the operator because "is" and "is blank" are both '=' — only the phrase tells them apart
  const [operatorPhrase, setOperatorPhrase] = useState('');
  const [singleValue, setSingleValue] = useState('');
  const [multiValues, setMultiValues] = useState<string[]>([]);

  const { data: jobCategories } = useFetchCategoriesQuery({ terminalNumber });

  /** Job Category is a closed set like Leg Status, but its codes are per-terminal so they arrive from the API */
  const fieldOptions = useMemo(() => {
    if (!jobCategories?.length) return filterFieldOptions;

    const categoryValues = jobCategories.map(({ id, name }) => ({ value: id, label: `${id} - ${name}` }));

    return filterFieldOptions.map((option) => (option.key === 'JobCategory' ? { ...option, values: categoryValues } : option));
  }, [jobCategories]);

  const fieldNames = useMemo(
    () => fieldOptions.filter((option) => !option.isDisabledAdding).map((option) => option.displayName),
    [fieldOptions]
  );

  // Held by key rather than by object so the selection survives the Job Category values arriving
  const field = useMemo(() => fieldOptions.find((option) => option.key === fieldKey), [fieldOptions, fieldKey]);

  const operatorOptions = useMemo(() => (field ? getOperatorOptions(field) : []), [field]);
  const operatorPhrases = useMemo(() => operatorOptions.map(({ phrase }) => phrase), [operatorOptions]);

  // Undefined while no field is picked, and after a field change drops the phrase the previous one offered
  const operatorOption = useMemo(() => operatorOptions.find((option) => option.phrase === operatorPhrase), [operatorOptions, operatorPhrase]);
  const operator = operatorOption?.operator ?? '=';
  const isBlank = operatorOption?.isBlank ?? false;

  const isMultiValue = isMultiValueOperator(operator);

  /**
   * MUI compares an Autocomplete's value by identity and clears the input whenever it changes, so a selection array
   * rebuilt on every render erases the text as it is typed — for a lookup field that means the search never runs.
   */
  const lookupIds = useMemo(() => {
    if (isBlank) return [blankLookupItem.id];
    if (isMultiValue) return multiValues;

    return singleValue ? [singleValue] : [];
  }, [isBlank, isMultiValue, multiValues, singleValue]);

  const closedSetSelection = useMemo(
    () => (field?.values ?? []).filter((option) => multiValues.includes(option.value)),
    [field, multiValues]
  );

  /** Blank leads a closed set the same way it leads a lookup, and the identity has to hold for the same reason */
  const closedSetOptions = useMemo(() => {
    if (!field?.values) return [];

    return field.canBeBlank ? [blankValueOption, ...field.values] : field.values;
  }, [field]);

  const draftFilter = useMemo<QueryFilter | undefined>(() => {
    if (!field) return undefined;
    // No value at all is what the API reads as absence, and JSON.stringify drops the key on the way out
    if (isBlank) return { map: field.key, operator, value: undefined };

    const value = buildFilterValue(field, operator, singleValue, multiValues);

    return value === undefined ? undefined : { map: field.key, operator, value };
  }, [field, isBlank, operator, singleValue, multiValues]);

  const isDuplicate = draftFilter ? values.filters.some((existing) => isSameFilter(existing, draftFilter)) : false;

  const resetValueInputs = (nextField: FilterFieldOption | undefined) => {
    // A boolean filter has no empty state, so it opens on Yes rather than on nothing
    setSingleValue(nextField?.fieldType === 'boolean' ? booleanOptions[0] : '');
    setMultiValues([]);
  };

  /** Picking a real value out of a blank filter lands on the equality operator that takes one. */
  const leaveBlank = () => {
    if (isBlank) setOperatorPhrase(getValueOperatorPhrase(operatorOptions));
  };

  /** Blank takes no value, so anything half-entered goes with it rather than reappearing on the way back out. */
  const enterBlank = () => {
    setOperatorPhrase(blankOperatorOption.phrase);
    resetValueInputs(field);
  };

  const handleFieldChange = (displayName: string) => {
    const nextField = fieldOptions.find((option) => option.displayName === displayName);

    setFieldKey(nextField?.key ?? null);
    // Every field type leads its operator list with '=', so the first option is always a valid landing point
    setOperatorPhrase(nextField ? getOperatorOptions(nextField)[0].phrase : '');
    resetValueInputs(nextField);
  };

  const handleAdd = () => {
    if (!draftFilter || isDuplicate) return;

    void setFieldValue('filters', [...values.filters, draftFilter], true);
    resetValueInputs(field);
  };

  const handleRemove = (index: number) => {
    void setFieldValue(
      'filters',
      values.filters.filter((_, filterIndex) => filterIndex !== index),
      true
    );
  };

  const renderValueInput = (selected: FilterFieldOption) => {
    // The pickers go on showing "(Blank)" as their selection; a plain input has no way to show it, so it comes off screen
    if (isBlank && !selected.values && !selected.lookup) return null;

    if (selected.fieldType === 'boolean') {
      return (
        <RadioGroupField
          labelText='Value'
          options={booleanOptions}
          value={singleValue}
          onChange={(event) => {
            setSingleValue(event.target.value);
          }}
        />
      );
    }

    if (selected.fieldType === 'date') {
      const offset = Number(singleValue);

      return (
        <div className={styles['filter-field']}>
          {/* precision 0 with negatives allowed is the day offset contract: the mask cannot produce a fraction */}
          <NumberField
            name='filter-day-offset'
            value={singleValue}
            onValueChange={(next) => {
              setSingleValue(next === null ? '' : String(next));
            }}
            customProps={{
              labelText: 'Days From Today',
              precision: 0,
              allowNegativeNumbers: true,
              onClear: () => {
                setSingleValue('');
              }
            }}
          />

          {/* NumberField has no helper text of its own, so the resolved date sits beneath it */}
          <span className={styles['filter-note']}>{singleValue === '' ? dayOffsetHint : formatDayOffset(offset)}</span>
        </div>
      );
    }

    if (selected.lookup) {
      return (
        <FilterValueLookup
          lookup={selected.lookup}
          label={isMultiValue ? 'Values' : 'Value'}
          multiple={isMultiValue}
          canBeBlank={selected.canBeBlank}
          value={lookupIds}
          onChange={(ids) => {
            if (ids[0] === blankLookupItem.id) {
              enterBlank();
              return;
            }

            leaveBlank();

            if (isMultiValue) {
              setMultiValues(ids);
            } else {
              setSingleValue(ids[0] ?? '');
            }
          }}
        />
      );
    }

    if (isMultiValue) {
      if (selected.values) {
        const options = selected.values;

        return (
          <AppMultiSelectChipComplex
            value={closedSetSelection}
            onValueChange={(next) => {
              setMultiValues((next ?? []).map((option) => (option as FilterValueOption).value));
            }}
            autoCompleteComplexItems={options}
            getOptionLabel={getValueOptionLabel}
            isOptionEqualToValue={isValueOptionEqual}
            renderOption={renderValueOption}
            placeholder='Select values'
            customProps={{ labelText: 'Values' }}
          />
        );
      }

      return (
        <AppMultiSelectChip
          value={multiValues}
          onValueChange={(next) => {
            setMultiValues((next ?? []).map((entry) => entry.trim()).filter(Boolean));
          }}
          placeholder='Type a value and press Enter'
          customProps={{ labelText: 'Values' }}
        />
      );
    }

    if (selected.values) {
      return (
        <AppComplexAutocomplete
          /**
           * Resolved against the real values only. singleValue is '' with nothing chosen, which is what the blank
           * option carries too — finding it there would show blank as already picked, and MUI compares an
           * Autocomplete's value by identity, so clicking the blank option would then change nothing.
           */
          value={isBlank ? blankValueOption : (selected.values.find((option) => option.value === singleValue) ?? null)}
          onValueChange={(next) => {
            const picked = next as FilterValueOption | null;

            if (picked?.value === blankPickerValue) {
              enterBlank();
              return;
            }

            leaveBlank();
            setSingleValue(picked?.value ?? '');
          }}
          autoCompleteComplexItems={closedSetOptions}
          getOptionLabel={getValueOptionLabel}
          isOptionEqualToValue={isValueOptionEqual}
          renderOption={renderValueOption}
          placeholder='Select a value'
          customProps={{ labelText: 'Value' }}
        />
      );
    }

    if (selected.fieldType === 'number') {
      return (
        <NumberField
          name='filter-number-value'
          value={singleValue}
          onValueChange={(next) => {
            setSingleValue(next === null ? '' : String(next));
          }}
          customProps={{
            labelText: 'Value',
            precision: 0,
            onClear: () => {
              setSingleValue('');
            }
          }}
        />
      );
    }

    return (
      <TextField
        value={singleValue}
        onChange={(event) => {
          setSingleValue(event.target.value);
        }}
        customProps={{
          labelText: 'Value',
          onClear: () => {
            setSingleValue('');
          }
        }}
      />
    );
  };

  return (
    <>
      <div className={styles['query-filters']}>
        <span className={styles['section-title']}>Query Filters ({values.filters.length})</span>

        {/* The create-only "at least one filter" rule is an array-level error, so it has no field of its own to land on */}
        {typeof errors.filters === 'string' && <span className={styles['filter-error']}>{errors.filters}</span>}

        <ul className={styles['filter-list']}>
          {values.filters.length === 0 ? (
            <li className={styles['filter-empty']}>No Filters — Every Undelivered Leg Is Shown</li>
          ) : (
            values.filters.map((filter, index) => (
              <li
                // Repeats of the same field are legitimate, so position is the only thing that identifies a row
                key={`filter-${String(index)}`}
                className={styles['filter-item']}>
                <span className={styles['filter-text']}>{describeFilter(filter, fieldOptions)}</span>

                <IconButton
                  size='small'
                  className={styles['filter-remove']}
                  title='Remove this filter'
                  onClick={() => {
                    handleRemove(index);
                  }}>
                  <RemoveIcon fontSize='small' />
                </IconButton>
              </li>
            ))
          )}
        </ul>
      </div>

      <div className={styles['filter-builder']}>
        <span className={styles['section-title']}>Add Filter</span>

        <AppAutocomplete
          value={field?.displayName ?? ''}
          onValueChange={handleFieldChange}
          autoCompleteItems={fieldNames}
          placeholder='Select a field'
          customProps={{ labelText: 'Field' }}
        />

        {field && (
          <>
            <AppAutocomplete
              value={operatorOption?.phrase ?? ''}
              onValueChange={(phrase) => {
                const match = operatorOptions.find((option) => option.phrase === phrase);
                // Blurring off an unrecognized entry clears the input; the operator holds its last valid choice
                if (!match) return;

                if (match.isBlank) enterBlank();
                else setOperatorPhrase(phrase);
              }}
              autoCompleteItems={operatorPhrases}
              customProps={{ labelText: 'Operator' }}
            />

            {renderValueInput(field)}
          </>
        )}

        <Button
          variant='contained'
          disabled={!draftFilter || isDuplicate}
          startIcon={<AddIcon fontSize='small' />}
          onClick={handleAdd}>
          Add Filter
        </Button>

        {isDuplicate && <span className={styles['filter-hint']}>This filter is already applied.</span>}
      </div>
    </>
  );
}
